/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useFlags } from 'gatsby-plugin-launchdarkly';

export interface DatasourceNode {
  node: {
    name: string;
    value: string;
  };
}

export interface Datasources {
  [key: string]: {
    edges: DatasourceNode[];
  };
}

type DataSourceProviderProps = { children: ReactNode };
type DataSourceContextProps = {
  contextDatasources: Datasources | null;
  setContextDataSources: (contextDatasources: Datasources | null) => void;
};

const initialValue: DataSourceContextProps = {
  contextDatasources: null,
  setContextDataSources: _contextDatasources => undefined
};

const DataSourceContext = createContext<DataSourceContextProps>(initialValue);
const getABTestingPages = (datasources: Datasources | null) =>
  datasources?.['ab_testing']?.edges.reduce(
    (abTestingPagesMap, { node }) => ({
      ...abTestingPagesMap,
      [node.name]: node.value
    }),
    {}
  ) ?? {};

const DataSourceProvider = ({ children }: DataSourceProviderProps) => {
  const [contextDatasources, setContextDataSources] = useState<Datasources | null>(null);
  const { enablePageAbTesting } = useFlags();
  const datasources = useStaticQuery(
    graphql`
      query datasourceNationalAverages {
        ab_testing: allStoryblokDatasourceEntry(filter: { data_source: { eq: "ab-testing" } }) {
          edges {
            node {
              name
              value
            }
          }
        }
        fdic: allStoryblokDatasourceEntry(filter: { data_source: { eq: "fdic" } }) {
          edges {
            node {
              name
              value
            }
          }
        }
        ncua: allStoryblokDatasourceEntry(filter: { data_source: { eq: "ncua" } }) {
          edges {
            node {
              name
              value
            }
          }
        }
        financial_institutions: allStoryblokDatasourceEntry(filter: { data_source: { eq: "financial-institutions" } }) {
          edges {
            node {
              name
              value
            }
          }
        }
        inactive_products: allStoryblokDatasourceEntry(filter: { data_source: { eq: "inactive-products" } }) {
          edges {
            node {
              name
              value
            }
          }
        }
        bank_logo_links: allStoryblokDatasourceEntry(filter: { data_source: { eq: "bank-logo-links" } }) {
          edges {
            node {
              name
              value
            }
          }
        }
        savings_calculator_default_values: allStoryblokDatasourceEntry(
          filter: { data_source: { eq: "savings-calculator-default-values" } }
        ) {
          edges {
            node {
              name
              value
            }
          }
        }
        interest_rate_shortcodes: allStoryblokDatasourceEntry(
          filter: { data_source: { eq: "interest-rate-shortcodes" } }
        ) {
          edges {
            node {
              name
              value
            }
          }
        }
      }
    `
  );

  useEffect(() => {
    if (!contextDatasources) {
      setContextDataSources(datasources);
    }
  }, [contextDatasources, datasources]);

  let endPageUrl = '';
  let pagesForABtesting: { [key: string]: string } | null = null;

  if (typeof window !== 'undefined' && window.IntersectionObserver) {
    const url = window.location.href.split('/en-us/');
    endPageUrl = url[url.length - 1];
    pagesForABtesting = getABTestingPages(datasources);
  }

  useEffect(() => {
    // AB testing of the page
    if (enablePageAbTesting && pagesForABtesting?.[endPageUrl]) {
      window.location.replace(pagesForABtesting?.[endPageUrl]);
    }
  }, [enablePageAbTesting, endPageUrl, pagesForABtesting]);

  return (
    <DataSourceContext.Provider value={{ contextDatasources, setContextDataSources }}>
      {enablePageAbTesting !== undefined && children}
    </DataSourceContext.Provider>
  );
};

const useDatasources = () => useContext(DataSourceContext);

export { DataSourceProvider, useDatasources };
